import {
  AttachMoney as AttachMoneyIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { Box, Chip, Grid, IconButton, Stack, Typography } from '@mui/material';
import TableCard from 'lib/designSystem/Card/TableCard';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import { useAmountCalculations } from '../hooks/useAmountCalculations';
import { Request } from '../types';

interface AmountSummaryProps {
  request: Request;
  onExportClick: () => void;
}

const AmountSummary: React.FC<AmountSummaryProps> = ({
  request,
  onExportClick,
}) => {
  const { amountPendingAccounts, amountAppliedDocuments, totalToPay } =
    useAmountCalculations(request);

  return (
    <TableCard
      size="medium"
      enabledEffects={false}
      sx={{ boxShadow: 'none', background: 'transparent' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ width: '100%', mb: 2 }}
      >
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <AttachMoneyIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Resumen Montos
          </Typography>
          <Chip
            label="Devolución Pasivos"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem', mt: 0.5 }}
          />
        </Box>
        {totalToPay > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalToPay)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={onExportClick}
          sx={{
            color: 'primary.main',
            backgroundColor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            '&:hover': {
              backgroundColor: 'grey.100',
              borderColor: 'grey.300',
            },
            borderRadius: '100px',
            padding: '4px 12px',
          }}
        >
          <FileDownloadIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
          <Typography variant="caption" fontWeight={600}>
            Exportar
          </Typography>
        </IconButton>
      </Stack>
      <Box
        sx={{
          backgroundColor: 'grey.50',
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Cuentas pendientes
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatMoney(amountPendingAccounts)} CLP
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Documentos aplicados
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatMoney(-amountAppliedDocuments)} CLP
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Total a pagar
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="primary.main"
              >
                {formatMoney(totalToPay)} CLP
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </TableCard>
  );
};

export default AmountSummary;
